/**
 * Edit | Preview for carer guidance in the catalogue item editor — shared by
 * a developmental goal's per-level Carer Guidance Prompts (2026-06-10, Ed)
 * and a monitored event's single Carer Guidance Prompts text (Zammad #49088,
 * Ed 2026-09-24: "consistent with D"). Preview is the default in both: you
 * read guidance far more often than you write it.
 *
 * Pure string builders, so both call sites render identical markup and the
 * one click handler in detail.ts (`[data-prompt-mode]`) drives both.
 */

import type { ParsedGuidanceLevel } from "./guidanceParser";
import { render as renderGuidance } from "./guidanceRenderer";
import { escapeHtml } from "../utils/html";

/**
 * The segmented Edit | Preview mini-buttons. `key` identifies what is being
 * toggled — a D level index ("0".."4") or "event" for monitored-event
 * guidance — and comes back to the click handler as `data-prompt-level`.
 * The active side is filled.
 */
export function editPreviewToggleHtml(key: string, inPreview: boolean): string {
  const btn = (mode: "edit" | "preview", label: string, active: boolean) =>
    `<button type="button" data-prompt-mode="${mode}" data-prompt-level="${escapeHtml(key)}" style="font-size:11px;padding:2px 8px;cursor:pointer;border:1px solid #d1d5db;background:${active ? "#7c3aed" : "#fff"};color:${active ? "#fff" : "#374151"};${mode === "edit" ? "border-radius:6px 0 0 6px;border-right:none;" : "border-radius:0 6px 6px 0;"}">${label}</button>`;
  return `<span style="display:inline-flex;margin-left:auto;">${btn("edit", "Edit", !inPreview)}${btn("preview", "Preview", inPreview)}</span>`;
}

/**
 * The rendered fold preview of one guidance text. An empty parse (no preamble
 * and no sections) falls back to the raw text in a `.level-text` div, so
 * authoring-in-progress text still shows.
 */
export function guidancePreviewHtml(
  parsed: ParsedGuidanceLevel,
  level: number,
  collapsedSet: Set<string>,
  rawText: string,
): string {
  return renderGuidance(parsed, level, collapsedSet)
    || `<div class="level-text">${escapeHtml(rawText)}</div>`;
}
